import React, { useState, useEffect, useRef } from 'react';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import MovieItem from '../components/MovieItem';
import StatusItem from '../components/StatusItem';
import Spinner from '../components/Spinner';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { toast } from 'react-toastify';

export default function Home() {
  const auth = getAuth();
  const [movies, setMovies] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const statusListRef = useRef(null);

  // Fetch movies and statuses
  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch movies
        const moviesRef = collection(db, 'movies');
        const moviesQuery = query(moviesRef, orderBy('timestamp', 'desc'));
        const moviesSnap = await getDocs(moviesQuery);
        const movieList = moviesSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setMovies(movieList);

        // Fetch statuses
        const statusRef = collection(db, 'status');
        const statusQuery = query(statusRef, orderBy('timestamp', 'desc'));
        const statusSnap = await getDocs(statusQuery);
        const currentTime = new Date();
        const statusList = statusSnap.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
            viewedBy: doc.data().viewedBy || []
          }))
          // Filter out statuses older than 24 hours
          .filter(status => {
            const timestamp = status.timestamp?.toDate();
            if (!timestamp) return false;
            const timeDiff = (currentTime - timestamp) / (1000 * 60 * 60); // Hours difference
            return timeDiff <= 24;
          });

        // Sort statuses: unviewed first, then by timestamp (latest first)
        const sortedStatuses = statusList.sort((a, b) => {
          const aViewed = a.viewedBy.includes(auth.currentUser?.uid);
          const bViewed = b.viewedBy.includes(auth.currentUser?.uid);
          if (aViewed && !bViewed) return 1; // Viewed statuses go last
          if (!aViewed && bViewed) return -1; // Unviewed statuses go first
          return b.timestamp.toDate() - a.timestamp.toDate(); // Latest first
        });

        setStatuses(sortedStatuses);
      } catch (error) {
        toast.error('Failed to load content');
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [auth.currentUser]);

  // Scroll the status list left or right
  const scrollLeft = () => {
    if (statusListRef.current) {
      statusListRef.current.scrollBy({ left: -150, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (statusListRef.current) {
      statusListRef.current.scrollBy({ left: 150, behavior: 'smooth' });
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <main className="min-h-screen py-2 px-2 sm:px-4 bg-black">
      {/* Status Section (if statuses are present) */}
      {statuses.length > 0 && (
        <>
          {/* Status Title */}
          <h2 className="text-gray-400 text-lg font-medium px-4">Status</h2>

          {/* Status List with Navigation Icons */}
          <div className="relative">
            {/* Left Navigation Icon */}
            <button
              onClick={scrollLeft}
              className="absolute left-0 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition duration-300 z-10 focus:outline-none"
              aria-label="Scroll statuses left"
            >
              <FaChevronLeft size={24} />
            </button>

            {/* Status List */}
            <div
              ref={statusListRef}
              className="flex overflow-x-auto py-2 px-4 bg-transparent scrollbar-hide"
            >
              {statuses.map(status => (
                <StatusItem key={status.id} status={status} />
              ))}
            </div>

            {/* Right Navigation Icon */}
            <button
              onClick={scrollRight}
              className="absolute right-0 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition duration-300 z-10 focus:outline-none"
              aria-label="Scroll statuses right"
            >
              <FaChevronRight size={24} />
            </button>
          </div>

          {/* Gray Divider */}
          <div className="border-t border-gray-700 my-1 mx-4" />
        </>
      )}

      {/* Movies Section */}
      <div>
        {/* Suggested for You Title (if statuses are present) */}
        {statuses.length > 0 && (
          <h2 className="text-gray-400 text-lg font-medium px-4 mb-3">Suggested for you</h2>
        )}

        {/* Movie Grid */}
        <div className="movie-grid">
          {movies.length > 0 ? (
            movies.map(movie => (
              <MovieItem key={movie.id} movie={movie} />
            ))
          ) : (
            <p className="text-gray-400 text-center w-full">No movies fetched, check your internet connection!</p>
          )}
        </div>
      </div>
    </main>
  );
}

// CSS to hide scrollbar (add to your global styles or a style tag)
const styles = `
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }

  .scrollbar-hide {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;  /* Firefox */
  }
`;

// Inject styles
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);